import { useParams, Link } from 'react-router-dom';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { articleId } = useParams<{ articleId: string }>();
  const article = articles.find((a) => a.id === articleId);

  useDocumentMeta(
    article ? `${article.title} - 九游娱乐资讯` : '文章详情 - 九游娱乐',
    article?.summary || '九游娱乐资讯文章'
  );

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-24 pb-20">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
              </div>
              <h1 className="text-xl font-bold text-foreground-900 mb-2">文章不存在</h1>
              <Link to="/articles" className="text-primary-600 hover:text-primary-700 text-sm">
                返回资讯中心
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.image,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '九游娱乐',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.jiuyouyule.com/' },
      { '@type': 'ListItem', position: 2, name: '资讯中心', item: 'https://www.jiuyouyule.com/articles' },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <article>
            <section className="pt-24 pb-10 bg-background-100">
              <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
                <Breadcrumb items={[
                  { label: '资讯中心', href: '/articles' },
                  { label: article.categoryName },
                ]} />
                <div className="mt-6">
                  <span className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-700 text-xs font-medium mb-4 inline-block">
                    {article.categoryName}
                  </span>
                  <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
                    {article.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                    <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                    <span><i className="ri-calendar-line mr-1"></i>{article.publishDate}</span>
                    <span><i className="ri-eye-line mr-1"></i>{article.readCount} 阅读</span>
                  </div>
                </div>
              </div>
            </section>

            <section className="w-full py-10 bg-background-50">
              <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
                {article.image && (
                  <div className="aspect-[21/9] rounded-xl overflow-hidden mb-10 border border-background-200/70">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <div
                  className="prose prose-base max-w-none text-foreground-800 leading-relaxed
                    [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-4
                    [&_p]:text-foreground-700 [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-sm md:[&_p]:text-base
                    [&_strong]:text-foreground-900 [&_strong]:font-semibold"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-background-200/70">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </section>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full py-14 bg-background-100">
              <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
                <h2 className="text-xl font-bold text-foreground-950 mb-6">相关文章</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img src={ra.image} alt={ra.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {ra.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-2">{ra.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
    </>
  );
}